import { Component, useState, useEffect, createContext, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  withRouter,
  useHistory,
} from "react-router-dom";

import {
  CssBaseline,
  ThemeProvider,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";

import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security, SecureRoute, LoginCallback } from "@okta/okta-react";

import { getConfig } from "./config";
import { TrackRecordsContainer } from "./components/TrackRecordsContainer";
import Header from "./components/Header";
import Login from "./components/Login";
import {
  GetFieldsWithValuesCashflows,
  GetAggregateByFields,
  GetFieldsWithValues,
} from "./API/TrackRecord";

import { lightTheme } from "./styles/Theme";
import { GENERIC_ERROR_MESSAGE, appErrorAlertSx } from "./constants/errorDisplay";

const appConfig = await getConfig();
export const ThemeContext = createContext();

function AppComponent() {
  const history = useHistory();

  const [appBusinessUnit, setAppBusinessUnit] = useState("");
  const [userBU, setUserBU] = useState(null);
  const [searchType, setSearchType] = useState("advanced");
  const [theme, setTheme] = useState(lightTheme);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [roaAggData, setRoaAggData] = useState([]);
  const [roaFieldData, setRoaFieldData] = useState([]);
  const [cashflowFieldData, setCashflowFieldData] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [errorComponent, setErrorComponent] = useState(null);

  const isCashflow = useMemo(() => searchType.startsWith("cs_"), [searchType]);

  const oktaAuth = new OktaAuth({
    issuer: appConfig.OKTA_ISSUER,
    clientId: appConfig.OKTA_CLIENT_ID,
    redirectUri: `${window.location.origin}/login/callback`,
    responseType: "id_token",
    scopes: ["openid", "profile", "groups", "email"],
  });

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  const showError = () => (
    <Alert severity="error" variant="outlined" sx={appErrorAlertSx}>
      <AlertTitle>Error</AlertTitle>
      {GENERIC_ERROR_MESSAGE}
    </Alert>
  );

  const loadData = async () => {
    setIsLoading(true);
    setErrorComponent(null);

    try {
      const [roaAgg, roaFields, cashFields] = await Promise.all([
        GetAggregateByFields(appBusinessUnit),
        GetFieldsWithValues(appBusinessUnit),
        GetFieldsWithValuesCashflows(appBusinessUnit),
      ]);
      setRoaAggData(roaAgg);
      setRoaFieldData(roaFields);
      setCashflowFieldData(cashFields);
    } catch (error) {
      const devPayload = {
        message: error?.message,
        name: error?.name,
        errorType: error?.errorType,
        detail: error?.detail,
        statusCode: error?.statusCode,
      };
      const m = error?.message || "";
      const unreachable =
        m.includes("Failed to fetch") ||
        m.includes("NetworkError") ||
        m.includes("Load failed");
      if (unreachable) {
        console.error(
          "[scenario: backend-unreachable]",
          "API did not respond (check API_BASE_URL, VPN, CORS).",
          devPayload
        );
      } else if (error?.errorType === "DUCKDB_QUERY_FAILED") {
        console.error(
          "[scenario: parquet-data]",
          "DuckDB/parquet issue on initial load (unusual — check API logs).",
          devPayload
        );
      } else if (error?.errorType === "DATABASE_UNAVAILABLE") {
        console.error(
          "[scenario: database]",
          "SQL Server unavailable (pyodbc / iHub or DW connection).",
          devPayload
        );
      } else {
        console.error(
          "[scenario: initialDataLoad]",
          "Initial field/aggregate load failed (see errorType/detail if present).",
          devPayload
        );
      }
      setErrorComponent(showError());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!appBusinessUnit) return;

    if (appBusinessUnit === "Unsupported") {
      setErrorComponent(
        <div className="App-body">
          <Typography variant="h4">
            Unsupported Business Unit. Please select a valid Business Unit.
          </Typography>
        </div>
      );
    } else if (appConfig.BUSINESS_UNITS.includes(appBusinessUnit)) {
      loadData();
    }
  }, [appBusinessUnit]);

  const renderMainView = () => {
    const isDataReady = !isLoading && roaAggData.length && roaFieldData.length && cashflowFieldData.length;
    if (errorComponent) return errorComponent;

    if (!isDataReady) {
      return (
        <div className="App-body">
          <CircularProgress size={60} />
        </div>
      );
    }

    return (
      <TrackRecordsContainer
        appBusinessUnit={appBusinessUnit}
        searchType={searchType}
        setSearchType={setSearchType}
        isCashflow={isCashflow}
        aggData={isCashflow ? [] : roaAggData}
        fieldData={isCashflow ? cashflowFieldData : roaFieldData}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
      />
    );
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="App" style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "auto" }}>
          <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <Header
              appBusinessUnit={appBusinessUnit}
              setAppBusinessUnit={setAppBusinessUnit}
              userBU={userBU}
              setUserBU={setUserBU}
              searchType={searchType}
              sidebarCollapsed={sidebarCollapsed}
              setSidebarCollapsed={setSidebarCollapsed}
            />
            <Route path="/login" exact component={Login} />
            <SecureRoute path="/" exact render={renderMainView} />
            <Route path="/login/callback" component={LoginCallback} />
          </Security>
        </div>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

const AppWithRouterAccess = withRouter(AppComponent);

class App extends Component {
  render() {
    return (
      <Router>
        <AppWithRouterAccess />
      </Router>
    );
  }
}

export default App;
